import type { Margin } from '@oneaudi/unified-web-common';
import { Checkbox, TextLink } from '@oneaudi/unified-web-components';
import type { FC } from 'react';
import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import type { CompareCarline } from '../../context/CompareContext';
import { CompareContext } from '../../context/CompareContext';
import { createCompareCheckboxId } from '../../context/compareContextUtils';
import { useContent } from '../../context/ContentContext';
import { useI18n } from '../../i18n';
import { useCompareTrackingManager } from '../../hooks/useCompareTrackingManager';

const SkipLink = styled(TextLink)`
  background-color: ${({ theme }) => theme.colors.backgroundCanvas};
  position: absolute;
  left: -9999px;

  &:focus {
    left: 0;
  }
`;

export type CompareCheckboxProps = {
  readonly carline: CompareCarline;
};

export const CompareCheckbox: FC<CompareCheckboxProps> = ({ carline }) => {
  const { compareMode } = useContent();
  const { state, dispatch } = useContext(CompareContext);
  const checked = useMemo(() => !!state.carlines.find(({ id }) => id === carline.id), [state]);
  const label = useI18n('compare.checkboxLabel');
  const ariaLabel = useI18n('compare.checkboxAriaLabel', {
    carlineName: carline.name,
  });
  const skipToCompareText = useI18n('compare.skipToCompareText');
  const cbId = createCompareCheckboxId(carline.id);
  const margin: Margin | undefined =
    compareMode === 'on' ? [0, 0, 'var(--spacing-relative-lg)', 0] : undefined;
  const { add: trackAdd, remove: trackRemove } = useCompareTrackingManager();

  return (
    <>
      <Checkbox
        id={cbId}
        name={cbId}
        label={label}
        aria-label={ariaLabel}
        value={carline.id}
        onChange={(e) => {
          if (e.target.checked === true) {
            trackAdd({ carline, label });
            dispatch({ type: 'add', carline });
          } else {
            trackRemove({ carline, label, elementName: 'check box' });
            dispatch({ type: 'remove', carline });
          }
        }}
        checked={checked}
        disabled={!checked && state.carlines.length >= state.amount}
        margin={margin}
      />
      {checked && (
        <SkipLink href="#compare-toggle" size="sm" variant="standalone">
          {skipToCompareText}
        </SkipLink>
      )}
    </>
  );
};
